import React, { useState, useEffect } from 'react';
import { Button, Container, Stack, Form, Row, Col, Accordion, Card } from 'react-bootstrap';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useGetJobAssessmentQuery, useSaveJobQuestionnaireMutation } from 'services';
import { useNavigate, useParams } from 'react-router-dom';
import '../CreateQuestionnaire/styles.scss';
import toast from 'react-hot-toast';
import useTranslate from 'hooks/useTranslate';

interface Question {
	id: number;
	question: string;
	options: string[];
}

interface CustomToggleProps {
	eventKey: string;
	children: React.ReactNode;
	onClick: (eventKey: string) => void;
}

const CustomToggle: React.FC<CustomToggleProps> = ({ eventKey, children, onClick }: any) => {
	const decoratedOnClick = useAccordionButton(eventKey, () => onClick(eventKey));

	return (
		<Button className="edit-btn border-0 rounded-circle p-0" onClick={decoratedOnClick}>
			{children}
		</Button>
	);
};

const EditQuestionnaire: React.FC = () => {
	const { translate } = useTranslate();
	const [count, setCount] = useState<number>(0);
	const { id } = useParams<any>();
	const navigate = useNavigate();

	const [questions, setQuestions] = useState<Question[]>([
		{ id: Date.now(), question: '', options: ['', '', '', ''] },
	]);
	const [activeKey, setActiveKey] = useState<string | null>(null);

	const { mutateAsync: saveQuestionnaire } = useSaveJobQuestionnaireMutation();
	const {
		data: { data: { jobQuestionList: { assessmentQuestions = [] } = {} } = {} } = {},
		isPending,
	} = useGetJobAssessmentQuery(id, false);

	useEffect(() => {
		if (assessmentQuestions?.length > 0) {
			const loadedQuestions = assessmentQuestions?.map((question: any) => ({
				id: question.id,
				question: question.question,
				options: question.options,
			}));
			setQuestions(loadedQuestions);
			formik.setValues({
				questions: loadedQuestions,
			});
			setActiveKey((loadedQuestions?.length - 1).toString());
		}
	}, [assessmentQuestions]);

	const validationSchema = Yup.object().shape({
		questions: Yup.array().of(
			Yup.object().shape({
				question: Yup.string()
					.required(
						translate('Validation.required', { field: translate('Questionnaire.question') }),
					)
					.trim(),
				options: Yup.array()
					.of(
						Yup.string()
							.required(translate('Validation.required', { field: translate('Extras.option') }))
							.trim(),
					)
					.min(4, translate('Extras.addAllOptions')),
			}),
		),
	});

	const formik: any = useFormik({
		initialValues: {
			questions: questions?.map((ques) => ({
				question: ques.question,
				options: ques.options,
			})),
		},
		validationSchema,
		onSubmit: (values) => {
			const updatedValue = values.questions?.map((obj) => {
				const newObj: any = { ...obj };
				delete newObj.id;
				return newObj;
			});

			saveQuestionnaire({
				totalQuestion: updatedValue?.length,
				jobId: id,
				questionArray: updatedValue,
			}).then((res: any) => {
				if (res?.success) {
					formik.resetForm();
					toast.success(res?.message);
					navigate(-1);
				}
			});
		},
	});

	useEffect(() => {
		formik.validateForm();
	}, []);

	const addQuestion = () => {
		if ((count === 0 || formik.isValid) && !isPending && questions?.length < 10) {
			setCount((prev) => prev + 1);
			const newId = Date.now();
			const newQuestion: Question = { id: newId, question: '', options: ['', '', '', ''] };
			setQuestions([...questions, newQuestion]);
			formik.setValues({
				...formik.values,
				questions: [...formik.values.questions, newQuestion],
			});
			setActiveKey(questions?.length?.toString());
		}
	};

	const deleteQuestion = (id: number) => {
		if (questions?.length > 1) {
			const updatedQuestions = formik.values.questions.filter((ques: any) => ques.id !== id);
			setQuestions(updatedQuestions);
			formik.setValues({
				...formik.values,
				questions: updatedQuestions,
			});
			setActiveKey((updatedQuestions?.length - 1).toString());
		}
	};

	const handleOptionChange = (questionIndex: number, optionIndex: number, value: string) => {
		const updatedQuestions = [...questions];
		updatedQuestions[questionIndex].options[optionIndex] = value;
		setQuestions(updatedQuestions);
		formik.setFieldValue(`questions.${questionIndex}.options.${optionIndex}`, value);
	};

	const handleToggle = (key: string) => {
		setActiveKey(activeKey === key ? null : key);
	};

	return (
		<section className="create-questionnaire py-50">
			<Container>
				<Form onSubmit={formik.handleSubmit}>
					<Stack className="create-questionnaire-inner">
						<div className="create-questionnaire-head d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
							<div>
								<h4 className="fw-bold">{translate('Questionnaire.editQuestionnaire')}</h4>
								<p className="mb-0">{translate('Questionnaire.addQuestions')}</p>
							</div>
						</div>
						<Accordion activeKey={activeKey} className="create-questionnaire-box">
							{questions?.map((ques, index) => (
								<Card key={`edit-ques-${ques.id}-${ques.question}`}>
									<Card.Header>
										<div className="d-flex gap-2 gap-lg-3 w-100">
											<div className="create-questionnaire-question flex-1">
												<Form.Group className="position-relative">
													<Form.Control
														type="text"
														placeholder={translate('Extras.writeQuestion')}
														name={`questions.${index}.question`}
														className="rounded-pill"
														autoComplete="off"
														value={formik.values.questions[index]?.question}
														onChange={(e: any) => {
															let inputValue = e.target.value;

															// Remove leading spaces if present
															inputValue = inputValue.replace(/^\s+/, '');

															// Allow only one space between words
															inputValue = inputValue.replace(/\s{2,}/g, ' ');
															formik.setFieldValue(`questions.${index}.question`, inputValue);
														}}
													/>
													{formik.touched.questions?.[index]?.question &&
														formik.errors.questions?.[index]?.question && (
															<div className="text-danger">
																{formik.errors.questions[index]?.question}
															</div>
														)}
												</Form.Group>
											</div>
											{questions?.length !== 1 && (
												<Button
													className="edit-btn border-0 rounded-circle p-0"
													onClick={() => deleteQuestion(ques.id)}
												>
													<i className="ri-delete-bin-7-line"></i>
												</Button>
											)}
											<CustomToggle eventKey={index.toString()} onClick={handleToggle}>
												<i className="ri-arrow-down-s-line"></i>
											</CustomToggle>
										</div>
									</Card.Header>
									<Accordion.Collapse eventKey={index.toString()}>
										<div className="create-questionnaire-options" id={`ques${index}`}>
											<Row>
												{ques?.options?.map((_option, optIndex) => (
													<Col md={6} key={optIndex}>
														<div className="option-box">
															<Form.Group className="position-relative">
																<Form.Control
																	type="text"
																	placeholder={`${translate('Extras.writeOption')} ${optIndex + 1}`}
																	name={`questions.${index}.options.${optIndex}`}
																	className="rounded-pill"
																	autoComplete="off"
																	value={formik.values.questions[index]?.options[optIndex]}
																	onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																		let inputValue = e.target.value;

																		// Remove leading spaces if present
																		inputValue = inputValue.replace(/^\s+/, '');

																		// Allow only one space between words
																		inputValue = inputValue.replace(/\s{2,}/g, ' ');
																		formik.setFieldValue(
																			`questions.${index}.options.${optIndex}`,
																			inputValue,
																		);
																		handleOptionChange(index, optIndex, inputValue);
																	}}
																/>
																{formik.touched.questions?.[index]?.options?.[optIndex] &&
																	formik.errors.questions?.[index]?.options?.[optIndex] && (
																		<div className="text-danger">
																			{formik.errors.questions[index]?.options[optIndex]}
																		</div>
																	)}
															</Form.Group>
														</div>
													</Col>
												))}
											</Row>
										</div>
									</Accordion.Collapse>
								</Card>
							))}
						</Accordion>
						<div className="d-flex justify-content-end gap-2 gap-md-3">
							<Button
								className="filled-btn rounded-pill"
								onClick={addQuestion}
								disabled={isPending || !formik.isValid || formik.values.questions.length === 10}
							>
								{translate('Questionnaire.addQuestion')}
							</Button>
							<Button className="filled-btn rounded-pill px-5 orange-bg" type="submit">
								{translate('Questionnaire.publish')}
							</Button>
						</div>
					</Stack>
				</Form>
			</Container>
		</section>
	);
};

export default EditQuestionnaire;
